import React, { useEffect, useState } from "react";
import { Card, ProgressBar } from "react-bootstrap";
import { PersonBadge } from "react-bootstrap-icons/dist";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { LOGIN_MODE, REGISTER_MODE } from "../../helpers/constants";
import {
  LOGIN_FORM_FIELDS,
  REGISTER_FORM_FIELDS,
  RESET_PASSWORD_FIELDS,
  VERIFY_EMAIL_FORM_FIELDS,
} from "../../helpers/forms";
import useAuth from "../../hooks/useAuth";
import ForgotPassword from "./ForgotPassword";
import LoginOrRegister from "./LoginOrRegister";
import ResetPassword from "./ResetPassword";

const Auth = () => {
  const {
    resetPassword,
    register,
    signIn,
    forgotPassword,
    generateRegistrationOTP,
    user,
    loggingIn,
    registrationOtpGenerated,
    passwordResetSuccessfull,
    forgotPasswordSuccessfull,
    updateRegistrationOTPvalue,
  } = useAuth();

  const [mode, setMode] = useState(LOGIN_MODE); // Login or Register Mode
  const [formInfo, setFormInfo] = useState({}); // Register or Login Form Info
  const [forgotPassModalMetadata, setForgotPassModalMetadata] = useState(null); // Forgot Pass Modal formInfo
  const [resetPasswordFormMetadata, setResetPasswordFormMetadata] = useState(
    null
  ); // Reset Pass Modal formInfo

  const onFormInfoChange = (key, value) => {
    formInfo[key] = value;
    setFormInfo({ ...formInfo });
  };

  const onModeChange = (value) => {
    setMode(value);
  };

  const onForgotPassModalChange = (value) => {
    setForgotPassModalMetadata(value);
  };

  const onForgotPassFieldValueChange = (key, value) => {
    forgotPassModalMetadata[key] = value;
    setForgotPassModalMetadata({ ...forgotPassModalMetadata });
  };

  const onResetPasswordFormMetadataChange = (key, value) => {
    resetPasswordFormMetadata[key] = value;
    setResetPasswordFormMetadata({ ...resetPasswordFormMetadata });
  };

  useEffect(() => {
    if (mode === REGISTER_MODE) {
      setFormInfo({});
    }

    updateRegistrationOTPvalue(false);
  }, [mode]);

  const auth = async () => {
    if (mode === REGISTER_MODE) {
      register({ ...formInfo, confirmPassword: undefined });
    } else {
      signIn(formInfo?.email, formInfo?.password);
    }
  };

  const onEmailVerification = async () => {
    if (!registrationOtpGenerated) {
      const emptyField = VERIFY_EMAIL_FORM_FIELDS.find(
        (field) => !formInfo[(field?.key)]
      );

      if (emptyField) {
        return toast.error(`${emptyField?.label} Is Empty`);
      }
      generateRegistrationOTP(formInfo?.email);
    }
  };

  const onLoginFormSubmit = async () => {
    const emptyField = LOGIN_FORM_FIELDS.find(
      (field) => !formInfo[(field?.key)]
    );

    if (emptyField) {
      return toast.error(`${emptyField?.label} Is Empty`);
    }

    await auth();
  };

  const onRegisterFormSubmit = async () => {
    const password = formInfo?.password;
    const confirmPassword = formInfo?.confirmPassword;

    const emptyField = REGISTER_FORM_FIELDS.find(
      (field) => !formInfo[(field?.key)]
    );

    if (emptyField) {
      return toast.error(`${emptyField?.label} Is Empty`);
    }

    if (password !== confirmPassword) {
      return toast.error("Passwords Didnot Match");
    }

    await auth();
  };

  const onForgotPasswordFormSubmit = async () => {
    if (!forgotPassModalMetadata?.email) {
      return toast.error("Email Is Empty");
    }
    forgotPassword(forgotPassModalMetadata?.email);
  };

  const onResetPasswordFormSubmit = async () => {
    const newPassword = resetPasswordFormMetadata?.newPassword;
    const confirmPassword = resetPasswordFormMetadata?.confirmPassword;

    const emptyField = RESET_PASSWORD_FIELDS.find(
      (field) => !resetPasswordFormMetadata[(field?.key)]
    );

    if (emptyField) {
      return toast.error(`${emptyField?.label} Is Empty`);
    }

    if (newPassword !== confirmPassword) {
      return toast.error(`Password Didnot Match.`);
    }

    resetPassword({
      email: forgotPassModalMetadata?.email,
      ...resetPasswordFormMetadata,
    });

    if (passwordResetSuccessfull) {
      setResetPasswordFormMetadata(null);
      setForgotPassModalMetadata(null);
    }
  };

  useEffect(() => {
    if (forgotPasswordSuccessfull) {
      setResetPasswordFormMetadata({ email: forgotPassModalMetadata?.email });
    }
  }, [forgotPasswordSuccessfull]);

  useEffect(() => {
    if (passwordResetSuccessfull) {
      setResetPasswordFormMetadata(null);
      setForgotPassModalMetadata(null);
    }
  }, [passwordResetSuccessfull]);

  return (
    <>
      {resetPasswordFormMetadata ? (
        <ResetPassword
          resetPasswordFormMetadata={resetPasswordFormMetadata}
          onResetPasswordFormMetadataChange={onResetPasswordFormMetadataChange}
          onHide={() => setResetPasswordFormMetadata(null)}
          onFormSubmit={onResetPasswordFormSubmit}
          forgotPassModalMetadata={forgotPassModalMetadata}
          onForgotPassFieldValueChange={onForgotPassFieldValueChange}
        />
      ) : (
        <ForgotPassword
          forgotPassModalMetadata={forgotPassModalMetadata}
          onHide={() => onForgotPassModalChange(null)}
          onForgotPassFieldValueChange={onForgotPassFieldValueChange}
          onFormSubmit={onForgotPasswordFormSubmit}
        />
      )}
      <div className="d-flex justify-content-center align-items-center">
        <Card className="w-50 mt-5">
          <Card.Body>
            <div className="d-flex justify-content-center">
              <h5 className="text-dark my-1">
                <PersonBadge className="mr-2 " />
                {mode === LOGIN_MODE
                  ? "Login"
                  : registrationOtpGenerated
                  ? "Register"
                  : "Verify Email"}
              </h5>
            </div>
            <hr />
            <LoginOrRegister
              formInfo={formInfo}
              onFormInfoChange={onFormInfoChange}
              fields={
                mode === LOGIN_MODE
                  ? LOGIN_FORM_FIELDS
                  : registrationOtpGenerated
                  ? REGISTER_FORM_FIELDS
                  : VERIFY_EMAIL_FORM_FIELDS
              }
              mode={mode}
              styles={styles}
              onModeChange={onModeChange}
              onForgotPassModalChange={onForgotPassModalChange}
              onFormSubmit={
                mode === LOGIN_MODE ? onLoginFormSubmit : onRegisterFormSubmit
              }
              onEmailVerification={onEmailVerification}
            />

            {loggingIn && (
              <ProgressBar
                className="mt-1"
                striped
                animated
                variant="dark"
                now={100}
                label={
                  mode === LOGIN_MODE
                    ? "Logging in..."
                    : registrationOtpGenerated
                    ? "Creating account..."
                    : "Sending OTP"
                }
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

const styles = {
  loginSignupText: {
    fontSize: 12,
    cursor: "pointer",
    textDecoration: "underline",
  },
};

export default Auth;
